import { runtimeConfig } from "config/runtimeConfig";
import React, { useContext, useEffect, useRef, useState } from "react";
import { toast } from "Components/DesignSystem/Toast/useToast";

import AuthManager from "../../../Common/AuthManager";
import { AppContext } from "../../../Context/AppContext";
import ServiceWrapper from "../../../Services/ServiceWrapper";
import ProcessingModeSelector from "./ProcessingModeSelector";

const NAME_TYPE_MAPPING = {
  availability: "AVAILABILITY_<YYYYMMDD>_<HHMMSS>.csv",
  products: "PRODUCTS_<YYYYMMDD>_<HHMMSS>.csv",
  // Underscore after PRICES was missing. midway_connector_svc matches uploads by splitting the
  // filename on "_" (SFTP/sftp_helper.py:45), so a file named to the old hint tokenised as
  // ["prices20260731", "120000"] and never matched the "prices" data type.
  prices: "PRICES_<YYYYMMDD>_<HHMMSS>.csv",
  invoice: "INVOICE_<YYYYMMDD>_<HHMMSS>.csv",
  relatedproducts: "RELATEDPRODUCTS_<YYYYMMDD>_<HHMMSS>.csv",
  orders: "ORDERS_<YYYYMMDD>_<HHMMSS>.csv",
  substitutes: "SUBSTITUTES_<YYYYMMDD>_<HHMMSS>.csv"
};

const DEFAULT_IMPORT_TYPE = "Data file feed";
const DEFAULT_DATA_TYPE = "products";

const SYNC_SAVE_DEBOUNCE_MS = 600;


const TAB_FEED_TYPES = {
  Products: "products",
  Orders: "orders",
  Price: "prices",
  Availability: "availability",
  "Related Products": "relatedproducts",
  Substitutes: "substitutes",
};

import ScheduleForm from "./ScheduleForm";
import SyncToEnvironments from "./SyncToEnvironments";
import { fetchConfiguration, updateConfiguration } from "./Services/configurationService";
import SidePanel from "Components/DesignSystem/SidePanel/SidePanel";
import Button from "Components/DesignSystem/Button/Button";
import Dropdown from "Components/DesignSystem/Dropdown/Dropdown";
import FieldTitle from "Components/DesignSystem/FieldTitle/FieldTitle";
import Typography from "Components/DesignSystem/Typography/Typography";
import { Cross } from "Components/DesignSystem/Iconography/Icons";

const MID_WAY_URL = runtimeConfig.CONNECTION_API;

const SCHEDULE_REQUIRED_WARNING =
  "Scheduled is selected as feed processing method, either configure the schedule or change feed processing method to proceed";

const IMPORT_TYPE_OPTIONS = [{ value: "Data file feed", label: "Data file feed" }];

/*
  The data-type keys must equal `midway_connector_svc`'s `FEED_TYPES` exactly — the
  import endpoint does NOT validate `data_type`, it falls through to `else: pass`, so a
  wrong key is accepted silently and nothing server-side will catch it. See
  `okf/connector/e2e.md` § 7.

  "relatedproducts" is the only key for related products: v1.0.1 was removed from the
  service's FEED_VERSIONS on 2026-08-05, so the once-planned version-driven mapping is
  dead. This was previously gated onto "accessories" by gms_endpoint < v3, which
  silently sent related-product data to the deprecated accessories feed: a different
  validator, a different CSV header set (productid/accessoryid/... vs
  sku/variantid/rel_sku/rel_variantid) and a different downstream endpoint
  (accessories/update vs related_products/update).
*/
const DATA_TYPE_OPTIONS = [
  { value: "products", label: "Products" },
  { value: "orders", label: "Orders" },
  { value: "prices", label: "Price" },
  { value: "availability", label: "Availability" },
  { value: "relatedproducts", label: "Related Products" },
  { value: "substitutes", label: "Substitutes" },
];

/**
 * The Data Import drawer — pick a feed type, trigger or schedule an import.
 *
 * On DS `SidePanel` since § 132. **Every handler is untouched**: `handleSubmit`,
 * `importData`, `validateForm`, `closeFormOnSuccess` and the two `AuthManager`
 * readers are byte-for-byte what they were, including the `prices → "Price"` and
 * `relatedproducts → "Related Products"` special cases in the tab the form lands on
 * after a successful import.
 *
 * What changed is the chrome:
 *
 * - The two native `<select className="form-select drop-down-style">` are DS
 *   `Dropdown`s. `.drop-down-style` was defined **twice** as a global class —
 *   `Connection.css` (`width: max(100%,335px)`) and `Admin/promotions/datepicker.css`
 *   (`width: 335px`) — and both stylesheets load whenever this drawer is open, so
 *   which one sized these two selects was decided by bundle import order. Exactly the
 *   hazard § 109 documented for `RulesLog.css`, still live.
 * - **`<label htmlFor="dataType">Data Type</label>` pointed at nothing**: the select's
 *   id is `data_type`. The label was decorative, clicking it did nothing, and the
 *   control was unnamed for assistive tech (WCAG 1.3.1, 3.3.2). `Dropdown` owns the
 *   association now.
 * - The filename hint was two `<label className="file-text">` elements with no
 *   `htmlFor` — labels labelling nothing — inside a hardcoded `#f2f2f2` box.
 *
 * One deliberate behaviour change, and it is small: the selects no longer carry an
 * empty `<option value="">Select</option>`, so a user cannot un-choose back to blank
 * once they have picked. `Select` is the placeholder now. Nothing depends on
 * returning to blank — `validateForm` rejects it and it exists only as the initial
 * state.
 */
const DataImportForm = (props) => {
  const { setShowImportDataForm, fetchData, currentTab } = props;
  const initialDataType = TAB_FEED_TYPES[currentTab] || DEFAULT_DATA_TYPE;
  const [selectedMode, setSelectedMode] = useState(null);
  const [nameType, setNameType] = useState(NAME_TYPE_MAPPING[initialDataType]);
  const [scheduledDataTypes, setScheduledDataTypes] = useState(null);


  // app context
  const appData = useContext(AppContext);

  const [formData, setFormData] = useState({
    type: DEFAULT_IMPORT_TYPE,
    data_type: initialDataType,
    user_name: get_logged_User_name(),
    user_id: get_logged_User_id(),
  });

  // Full connector config used as the merge base when persisting sync targets.
  const [existingConfig, setExistingConfig] = useState(null);
  // Emitted payload for `sku_sync_environments` from the SyncToEnvironments section.
  const [skuSyncEnvironments, setSkuSyncEnvironments] = useState([]);
  const [syncSelectionPending, setSyncSelectionPending] = useState(false);
  const [syncEdits, setSyncEdits] = useState(0);

  const canConfigureSync = appData.pm.checkPermission("gmsconnector_config");

  useEffect(() => {
    const loadConfig = async () => {
      if (!appData?.channelKey) return;
      const res = await fetchConfiguration(appData.channelKey);
      if (res.success && res.data) setExistingConfig(res.data);
    };
    loadConfig();
  }, [appData?.channelKey]);

  const persistSyncConfig = async (selection = skuSyncEnvironments) => {
    // Only products support environment sync, and only with config permission.
    if (formData.data_type !== "products" || !canConfigureSync) return false;
    if (!existingConfig || !appData?.channelKey) return false;

    const payload = {
      ...existingConfig,
      channel_key: appData.channelKey,
      sku_sync_environments: selection,
      updated_by_user_id: formData.user_id,
      updated_by_username: formData.user_name,
    };

    const res = await updateConfiguration(appData.channelKey, payload);
    if (!res.success) {
      toast.error(res.error || "Failed to save sync-to-environments settings.");
      return false;
    }
    return true;
  };

  const persistSyncConfigRef = useRef(persistSyncConfig);
  persistSyncConfigRef.current = persistSyncConfig;
  const latestSyncSelection = useRef(skuSyncEnvironments);
  latestSyncSelection.current = skuSyncEnvironments;

  useEffect(() => {
    if (syncEdits === 0 || syncSelectionPending || !existingConfig) return;
    const timer = setTimeout(async () => {
      if (await persistSyncConfigRef.current(latestSyncSelection.current)) {
        toast.success("Sync to environments updated");
      }
    }, SYNC_SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [syncEdits, syncSelectionPending, existingConfig]);

  // Feed Processing mode is scoped per channel + feed type. The feed type comes from
  // the active tab; if the user picks a specific Data Type to import, that wins.
  const feedType = formData.data_type || TAB_FEED_TYPES[currentTab] || "";

  const missingSelections = [
    !formData.type && "Import Type",
    !formData.data_type && "Data Type",
  ].filter(Boolean);
  const modeBlockedReason = missingSelections.length
    ? `Select ${missingSelections.join(" and ")} before choosing a feed processing mode.`
    : "";

  function get_logged_User_name() {
    let userobj = null;
    if (AuthManager.isAuthenticated()) userobj = AuthManager.getAuthUser();
    if (
      userobj === null ||
      userobj === undefined ||
      userobj.profile === null ||
      userobj.profile === undefined
    )
      return "guest";
    return userobj.profile.first_name + " " + userobj.profile.last_name;
  }

  function get_logged_User_id() {
    let userobj = null;
    if (AuthManager.isAuthenticated()) userobj = AuthManager.getAuthUser();
    if (
      userobj === null ||
      userobj === undefined ||
      userobj.profile === null ||
      userobj.profile === undefined
    )
      return null;
    return userobj.profile.id;
  }

  /**
   * Was `handleChange(e)`, reading `e.target.name` / `e.target.value`. DS `Dropdown`
   * reports the value directly, so the name comes from the call site instead of the
   * DOM. The body is otherwise identical.
   */
  const handleChange = (name, value) => {

    if (name === "data_type") {
      setNameType(NAME_TYPE_MAPPING[value]);
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      importData();
    }
  };

  // The ProcessingModeSelector persists the mode itself; we only track the active
  // mode here so the schedule configuration section is shown while Scheduled is
  // selected. (On a failed save the selector reverts the mode, hiding the section.)
  const handleModeChange = (mode) => setSelectedMode(mode);

  useEffect(() => {
    let cancelled = false;
    const loadSchedules = async () => {
      try {
        const res = await ServiceWrapper.doGet(
          `${MID_WAY_URL}connection/${appData?.channelKey}/schedule`,
          null,
          null,
          AuthManager.getGmsToken()
        );
        if (cancelled) return;
        const rows = Array.isArray(res?.data?.data) ? res.data.data : null;
        setScheduledDataTypes(rows ? new Set(rows.map((row) => row?.data_type)) : null);
      } catch {
        if (!cancelled) setScheduledDataTypes(null);
      }
    };
    loadSchedules();
    return () => {
      cancelled = true;
    };
  }, [appData?.channelKey]);

  const isMissingSchedule =
    selectedMode === "scheduled" &&
    !!scheduledDataTypes &&
    !scheduledDataTypes.has(feedType);

  const requestClose = () => {
    if (isMissingSchedule) {
      toast.warning(SCHEDULE_REQUIRED_WARNING);
      return;
    }
    setShowImportDataForm(false);
  };

  const closeFormOnSuccess = () => {
    setShowImportDataForm(false);
    let newDataType;
    switch (formData.data_type) {
      case "prices":
        newDataType = "Price";
        break;
      case "relatedproducts":
        newDataType = "Related Products";
        break;
      default:
        newDataType = formData.data_type.charAt(0).toUpperCase() + formData.data_type.slice(1)
    }
    props.setCurrentTab(newDataType);
  };

  const importData = async (data) => {
    await persistSyncConfig();
    let res = await ServiceWrapper.doPost(
      `${MID_WAY_URL}connection/${appData?.channelKey}/import`,
      data ?? formData,
      null,
      AuthManager.getGmsToken()
    );

    if (res?.data?.done) {
      toast.success("Data Import initialized.");
      closeFormOnSuccess();
    } else {
      toast.error(res?.data?.message ?? "Data import failed.");
    }
  };

  const validateForm = () => {
    if (formData.type === "" || formData.data_type === "") {
      toast.error("Import type, and Data type is mandatory.");
      return false;
    }
    return true;
  };

  return (
    <SidePanel
      isOpen
      onClose={requestClose}
      title="Data Import"
      tabs={[]}
      /* `md` (416px), not `lg`. Every control in here is capped at 335px, so `lg` left
         roughly a third of the panel as dead space to the right of the fields. The
         widest thing it has to hold is the Start Date + Start Time row, measured at
         ~358px against the 368px of content width `md` leaves after its padding. */
      width="md"
      data-testid="connectors-data-import"
      headerActions={
        <Button
          variant="tertiary"
          size="sm"
          aria-label="Close"
          data-testid="connectors-data-import-close-btn"
          onClick={requestClose}
        >
          <Cross size={16} />
        </Button>
      }
    >
      <form onSubmit={handleSubmit} className="tw-flex tw-flex-col tw-gap-4">
        <section
          className="tw-rounded-lg tw-border tw-border-edge tw-p-4"
          aria-labelledby="connectors-data-import-types-title"
          data-testid="connectors-data-import-types"
        >
          <span id="connectors-data-import-types-title" className="tw-text-base tw-font-semibold tw-text-ink">
            Source
          </span>
          <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-4">
            <Dropdown
              label="Import Type"
              id="type"
              name="type"
              options={IMPORT_TYPE_OPTIONS}
              value={formData.type}
              onChange={(value) => handleChange("type", String(value))}
              placeholder="Select"
              className="tw-max-w-[335px]"
              data-testid="connectors-data-import-type"
            />

            <Dropdown
              label="Data Type"
              id="data_type"
              name="data_type"
              options={DATA_TYPE_OPTIONS}
              value={formData.data_type}
              onChange={(value) => handleChange("data_type", String(value))}
              placeholder="Select"
              className="tw-max-w-[335px]"
              data-testid="connectors-data-import-data-type"
            />

            {/*
              The filename the user must upload to SFTP. Not a form field — it is output —
              so it is a labelled read-only block rather than two orphan `<label>`s.
            */}
            <div className="tw-max-w-[335px] tw-rounded-ctl tw-bg-surface-sunken tw-p-3">
              <FieldTitle label="File name" size="sm" className="tw-mb-1" />
              <Typography
                variant="body-primary"
                as="p"
                className="tw-text-ink-secondary tw-break-all"
                data-testid="connectors-data-import-filename"
              >
                {nameType}
              </Typography>
            </div>
          </div>
        </section>

        {formData.data_type === "products" && canConfigureSync && (
          <SyncToEnvironments
            partyId={appData?.selectedParty?.id}
            domainId={appData?.selectedIndustry?.id}
            initialValue={existingConfig?.sku_sync_environments}
            onChange={(value, meta) => {
              setSkuSyncEnvironments(value);
              setSyncSelectionPending(!!meta?.pending);
              setSyncEdits(meta?.edits ?? 0);
            }}
            currentChannelKey={appData?.channelKey}
          />
        )}

        {/* release-sept-29 (baa0b4880, PF-12707) replaced the Import Now / Schedule
            button pair with a persisted processing MODE. The buttons the design-system
            pass restyled no longer exist to restyle — `showSchedule` went with them —
            so what sits here is release's flow wearing this panel's chrome. */}
        {feedType && (
          <ProcessingModeSelector
            channelKey={appData?.channelKey}
            feedType={feedType}
            canEdit={appData.pm.checkPermission("gmsconnector_schedule")}
            blockedReason={modeBlockedReason}
            onModeChange={handleModeChange}
          />
        )}

        {selectedMode === "scheduled" && !modeBlockedReason && (
          <ScheduleForm
            validateForm={validateForm}
            formData={formData}
            fetchData={fetchData}
            submitForm={importData}
            persistSyncConfig={persistSyncConfig}
            closeForm={closeFormOnSuccess}
          />
        )}
      </form>
    </SidePanel>
  );
};

export default DataImportForm;
